/**
 * Demo data inject/cleanup for the Machines > Live Overview segment of the Welcome Tour.
 *
 * Injects:
 * - Demo machine into machines list + selectedItem (with latestMachineEvent for MachineActivityBlock)
 * - Demo batch statistics into batch reducer
 * - Demo timeline events + quantity events into registry (intercepted by reducer guards)
 * - Demo OEE/TEEP data into registry (intercepted by statistics reducer guard)
 * - Demo tools data into registry (intercepted by clampingTools reducer guard)
 */

import { AppThunk } from '@store/types';
import { setBatch } from '@reducers/batch';
import { setSelectedMachine, injectDemoMachine, removeDemoMachine } from '@reducers/machines';
import {
  registerDemoBatch,
  registerDemoTimelineEvents,
  registerDemoBatchSearch,
  registerDemoNcFileContent,
  registerDemoOeeTeep,
  registerDemoTools,
  registerDemoNCFileById,
  clearDemoRegistry,
  setTourActive,
} from '../../../../helpers/demoDataRegistry';
import { DEMO_WELCOME_MACHINE_ID, DEMO_BATCH_ID, DEMO_NC_FILE_ID } from '@components/tours/config/global/welcome/constants';
import {
  createDemoMachine,
  createDemoBatchStatistics,
  createDemoTimelineEvents,
  createDemoBatchSearchResult,
  createDemoNcFileContent,
  createDemoOeeTeepData,
  createDemoToolsData,
  createDemoNCFileById,
} from './fixtures';

// Store originals for restoration
let originalBatch: any = null;
let originalSelectedMachine: any = null;

/**
 * Inject demo data for the Machines > Live Overview segment.
 */
export const injectMachinesBatchDemo = (): AppThunk => (dispatch, getState) => {
  const state = getState();

  // Save originals for cleanup
  originalBatch = state.batch?.batch?.data;
  originalSelectedMachine = state.machines?.selectedItem;

  // Block real API calls (getMachines, updateMachineInfo, etc.) during tour
  setTourActive(true);

  // Register demo data in registry (reducers will intercept API calls)
  const batchStats = createDemoBatchStatistics();
  const timelineEvents = createDemoTimelineEvents();
  const batchSearchResult = createDemoBatchSearchResult();

  registerDemoBatch(DEMO_BATCH_ID, batchStats);
  registerDemoTimelineEvents(DEMO_BATCH_ID, timelineEvents);
  registerDemoBatchSearch(DEMO_BATCH_ID, batchSearchResult);
  registerDemoNcFileContent('demo-nc-file-hash-001', createDemoNcFileContent());
  registerDemoOeeTeep(DEMO_WELCOME_MACHINE_ID, createDemoOeeTeepData());
  registerDemoTools(DEMO_NC_FILE_ID, createDemoToolsData());
  registerDemoNCFileById(DEMO_NC_FILE_ID, createDemoNCFileById());

  // Inject demo machine (includes latestMachineEvent for MachineActivityBlock)
  const demoMachine = createDemoMachine();
  dispatch(injectDemoMachine(demoMachine));
  dispatch(setSelectedMachine(demoMachine));

  // Set batch data directly (for components that read from Redux without refetching)
  dispatch(setBatch(batchStats));
};

/**
 * Cleanup demo data after segment ends.
 */
export const cleanupMachinesBatchDemo = (): AppThunk => (dispatch) => {
  // Clear registry + tour active flag (stops intercepting API calls)
  clearDemoRegistry();
  setTourActive(false);

  // Remove demo machine
  dispatch(removeDemoMachine(DEMO_WELCOME_MACHINE_ID));

  // Restore original state
  if (originalSelectedMachine && originalSelectedMachine.machineId !== DEMO_WELCOME_MACHINE_ID) {
    dispatch(setSelectedMachine(originalSelectedMachine));
  } else {
    dispatch(setSelectedMachine({}));
  }

  if (originalBatch && originalBatch.productionBatchId !== DEMO_BATCH_ID) {
    dispatch(setBatch(originalBatch));
  } else {
    dispatch(setBatch(null));
  }

  // Clear stored refs
  originalBatch = null;
  originalSelectedMachine = null;
};
